/**
 * Toast Component
 * Displays temporary notification messages with auto-dismiss functionality
 */

// Toast configuration
const TOAST_CONFIG = {
  duration: 5000, // Default duration in ms
  position: 'top-right', // Default position
  containers: {}, // Multiple containers for different positions
  icons: {
    default: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`,
    information: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`,
    success: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>`,
    warning: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>`,
    danger: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>`
  },
  closeIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`
};

/**
 * Modale ouverte au premier plan (showModal) : hors d'elle, tout est inerte
 * (bouton de fermeture inutilisable, annonces aria-live ignorées).
 */
function topmostModal() {
  const open = [...document.querySelectorAll('dialog[open]')].filter((d) => {
    try { return d.matches(':modal'); } catch { return true; }
  });
  return open[open.length - 1] || null;
}

/**
 * Place les conteneurs dans la modale active (ou le body) et les (re)promeut au
 * top layer : sinon ils restent sous un <dialog> showModal() ouvert, z-index ou pas.
 */
function placeContainers() {
  const host = topmostModal() || document.body;
  Object.values(TOAST_CONFIG.containers).forEach((container) => {
    if (container.parentNode !== host) host.appendChild(container);
    if (typeof container.showPopover !== 'function') return;
    // Fermer puis rouvrir : repasse au-dessus de ce qui a été ouvert depuis.
    if (container.matches(':popover-open')) container.hidePopover();
    container.showPopover();
  });
}

// Suit l'ouverture/fermeture des modales pendant qu'un toast est affiché.
let dialogObserver = null;
function watchDialogs() {
  if (dialogObserver || typeof MutationObserver === 'undefined') return;
  dialogObserver = new MutationObserver((mutations) => {
    if (!Object.keys(TOAST_CONFIG.containers).length) return;
    if (mutations.some((m) => m.target.nodeName === 'DIALOG')) placeContainers();
  });
  dialogObserver.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
}

/**
 * Create or get the toast container for a specific position
 */
function getToastContainer(position = 'top-right') {
  // One container per position (all toast types stack together)
  if (!TOAST_CONFIG.containers[position]) {
    const container = document.createElement('div');
    container.className = `toast-container toast-container--${position}`;
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'false'); // Each toast announces individually
    container.dataset.position = position;
    if (typeof container.showPopover === 'function') container.setAttribute('popover', 'manual');
    TOAST_CONFIG.containers[position] = container;
    watchDialogs();
  }
  return TOAST_CONFIG.containers[position];
}

/**
 * Create a toast element
 */
function createToastElement(options) {
  const {
    title = '',
    message = '',
    type = 'default',
    duration = TOAST_CONFIG.duration,
    closable = true
  } = options;

  const toast = document.createElement('div');
  toast.className = `toast toast--${type}`;

  // Use role="alert" for urgent messages (danger, warning)
  const isUrgent = type === 'danger' || type === 'warning';
  toast.setAttribute('role', isUrgent ? 'alert' : 'status');

  // Each toast has its own aria-live to control announcement priority
  toast.setAttribute('aria-live', isUrgent ? 'assertive' : 'polite');
  toast.setAttribute('aria-atomic', 'true');

  // Add aria-label with type context for screen readers
  const typeLabels = {
    default: 'Notification',
    information: 'Information',
    success: 'Succès',
    warning: 'Avertissement',
    danger: 'Erreur'
  };
  const ariaLabel = `${typeLabels[type]}${title ? ': ' + title : ''}`;
  toast.setAttribute('aria-label', ariaLabel);

  // Get icon for the type
  const icon = TOAST_CONFIG.icons[type] || TOAST_CONFIG.icons.default;

  // Build toast HTML
  toast.innerHTML = `
    <div class="toast__progress" style="animation-duration: ${duration}ms;" aria-hidden="true"></div>
    <div class="toast__icon" aria-hidden="true">
      ${icon}
    </div>
    <div class="toast__content">
      ${title ? `<p class="toast__title">${title}</p>` : ''}
      ${message ? `<p class="toast__message">${message}</p>` : ''}
    </div>
    ${closable ? `<button class="toast__close" type="button" aria-label="Fermer la notification">${TOAST_CONFIG.closeIcon}</button>` : ''}
  `;

  return toast;
}

/**
 * Dismiss a toast
 */
function dismissToast(toast) {
  if (!toast || toast.classList.contains('toast--hide')) return;

  toast.classList.add('toast--hide');

  // Remove from DOM after animation
  setTimeout(() => {
    const container = toast.parentNode;
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
    // Remove container if empty
    if (container && container.children.length === 0) {
      container.remove();
      // Remove from containers registry
      for (const key in TOAST_CONFIG.containers) {
        if (TOAST_CONFIG.containers[key] === container) {
          delete TOAST_CONFIG.containers[key];
          break;
        }
      }
    }
  }, 200); // Match transition duration
}

/**
 * Show a toast notification
 */
function showToast(options) {
  const position = options.position || TOAST_CONFIG.position;
  const container = getToastContainer(position);
  const toast = createToastElement(options);

  // Add to container
  container.appendChild(toast);
  placeContainers();

  // Store timeout and pause state
  let timeoutId;
  let remainingTime = options.duration || TOAST_CONFIG.duration;
  let startTime = Date.now();
  let isPaused = false;

  // Auto-dismiss function
  function startAutoDismiss() {
    if (isPaused) return;

    startTime = Date.now();
    timeoutId = setTimeout(() => {
      dismissToast(toast);
    }, remainingTime);
  }

  // Pause on hover
  toast.addEventListener('mouseenter', () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      remainingTime -= Date.now() - startTime;
      isPaused = true;
    }
  });

  // Resume on mouse leave
  toast.addEventListener('mouseleave', () => {
    if (isPaused) {
      isPaused = false;
      startAutoDismiss();
    }
  });

  // Close button handler
  const closeBtn = toast.querySelector('.toast__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      if (timeoutId) clearTimeout(timeoutId);
      dismissToast(toast);
    });
  }

  // Start auto-dismiss
  startAutoDismiss();

  return toast;
}

/**
 * Initialize toast component
 * Expose Toast API globally for easy usage
 */
export function initToast() {
  // Expose Toast API on window object
  if (typeof window !== 'undefined') {
    window.Toast = {
      show: showToast,

      default(title, message, options = {}) {
        return showToast({ title, message, type: 'default', ...options });
      },

      information(title, message, options = {}) {
        return showToast({ title, message, type: 'information', ...options });
      },

      success(title, message, options = {}) {
        return showToast({ title, message, type: 'success', ...options });
      },

      warning(title, message, options = {}) {
        return showToast({ title, message, type: 'warning', ...options });
      },

      danger(title, message, options = {}) {
        return showToast({ title, message, type: 'danger', ...options });
      }
    };
  }
}
